// RFC-0014 Phase 1 Task 1.3 — mobile top bar (hamburger + page title, below md).
//
// A minimal top bar shown ONLY below the `md` breakpoint (via the CSS `md:hidden`
// variant — NOT gated on the JS viewport hook). It carries a hamburger button
// that opens the app-shell navigation drawer (Task 1.4) and the current page's
// title, derived from the route. RFC §1: hamburger + current-page title, no other
// chrome.

import { List } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import { useAppShellDrawer } from '@/context/app-shell-drawer';

/** Fallback title for unmatched / nested routes (e.g. /projects/:repo/...). */
const FALLBACK_TITLE = 'nmblr docs';

/**
 * Ordered route→title map for the app's top-level nav, checked most-specific
 * first. `/` matches exactly (exact: true); all others match on prefix so nested
 * routes under a section still resolve to the section title. Kept local to this
 * file on purpose — do NOT import the sidebar's NAV.
 */
const ROUTE_TITLES: ReadonlyArray<{ prefix: string; title: string; exact?: boolean }> = [
  { prefix: '/repositories', title: 'Repositories' },
  { prefix: '/sync-issues', title: 'Sync issues' },
  { prefix: '/search', title: 'Search' },
  { prefix: '/graph', title: 'Dependency graph' },
  { prefix: '/settings', title: 'Configuration' },
  { prefix: '/', title: 'Overview', exact: true },
];

function titleForPath(pathname: string): string {
  for (const { prefix, title, exact } of ROUTE_TITLES) {
    if (exact ? pathname === prefix : pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return title;
    }
  }
  return FALLBACK_TITLE;
}

/**
 * Mobile-only top bar: hamburger (opens the nav drawer) + current-page title.
 * Rendered at all viewports but hidden at `md`+ via the `md:hidden` CSS variant.
 */
export function MobileTopBar(): React.ReactElement {
  const { open } = useAppShellDrawer();
  const { pathname } = useLocation();
  const title = titleForPath(pathname);

  return (
    <div className="flex items-center gap-2 border-b border-border bg-surface px-2 md:hidden">
      <button
        type="button"
        onClick={open}
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text/70 transition-colors hover:bg-overlay/10 hover:text-text"
      >
        <List size={22} aria-hidden="true" />
      </button>
      <span className="truncate text-sm font-medium text-text">{title}</span>
    </div>
  );
}
